import { Component, OnInit, OnDestroy, Signal, computed, signal, WritableSignal, effect } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AnalysisDetailDialogComponent } from '../analysis-detail-dialog/analysis-detail-dialog.component';
import { CommonModule } from '@angular/common';
import { MaterialModule } from '@app/material.module';
import { ExternalDocumentService } from '@app/services/entities/external-document.service';
import { CustomSnackBarService } from '@app/shared/components/custom-snack-bar/custom-snack-bar.service';
import { CustomMessageService } from '@app/shared/components/custom-message/custom-message.service';
import { ExternalDocument, ProcessorType, PROCESSOR_OPTIONS } from '@app/types';
import { Router } from '@angular/router';
import { AnalysisService } from '@app/services/entities/analysis.service';
import { AnalysisQueueState } from '@app/types';
import { AnalysisStatusCardComponent } from '../analysis-status-card/analysis-status-card.component';
import { FormControl, FormGroup, ReactiveFormsModule, FormBuilder, FormsModule } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { Subject, takeUntil } from 'rxjs';
import { SelectionModel } from '@angular/cdk/collections'; // Importante para manejo de selección

@Component({
  selector: 'app-external-document-processing',
  standalone: true,
  imports: [CommonModule, MaterialModule, AnalysisStatusCardComponent, ReactiveFormsModule, MatButtonToggleModule, FormsModule],
  templateUrl: './external-document-processing.component.html',
  styleUrls: ['./external-document-processing.component.scss']
})
export class ExternalDocumentProcessingComponent implements OnInit, OnDestroy {
  // Signals existentes
  documentsSignal: Signal<ExternalDocument[]>;
  filteredDocsSignal: Signal<ExternalDocument[]>;
  paginatedDocsSignal: Signal<ExternalDocument[]>;
  isLoadingSignal: Signal<boolean>;
  analysisStatusSignal: Signal<AnalysisQueueState>;

  // Filtros
  folioFilterCtrl = new FormControl('');
  statusFilterCtrl = new FormControl('');
  startDateFilterCtrl = new FormControl<Date | null>(null);
  endDateFilterCtrl = new FormControl<Date | null>(null);
  filtroForm!: FormGroup;
  rangoFechasFormGroup!: FormGroup;

  private folioFilterSignal: WritableSignal<string> = signal('');
  private statusFilterSignal: WritableSignal<string> = signal('');
  private startDateFilterSignal: WritableSignal<Date | null> = signal(null);
  private endDateFilterSignal: WritableSignal<Date | null> = signal(null);

  // Pagination
  pageIndex: WritableSignal<number> = signal(0);
  pageSize: WritableSignal<number> = signal(10);

  // Processor Selection
  selectedProcessor: WritableSignal<ProcessorType> = signal('GPU');
  processorOptions = PROCESSOR_OPTIONS;

  // --- NUEVO: Gestión de Selección ---
  selection = new SelectionModel<string>(true, []); // true = multiple
  selectedCount = signal(0);
  isProcessingBatch = signal(false); // Spinner para acciones en lote

  // Signals de borrado
  deletingIds!: Signal<Set<string>>;
  isDeleting!: Signal<boolean>;

  private destroy$ = new Subject<void>();

  constructor(
    private externalDocumentService: ExternalDocumentService,
    private analysisService: AnalysisService,
    private snackBar: CustomSnackBarService,
    private router: Router,
    private fb: FormBuilder,
    private customMessage: CustomMessageService,
    private dialog: MatDialog
  ) {
    this.documentsSignal = this.externalDocumentService.externalDocumentsSignal;
    this.isLoadingSignal = this.externalDocumentService.isLoadingSignal;
    this.analysisStatusSignal = this.analysisService.state;
    this.deletingIds = this.externalDocumentService.deletingIds;
    this.isDeleting = this.externalDocumentService.isDeleting;

    // Inicialización de forms
    this.filtroForm = this.fb.group({
      folio: [''],
      estatus: ['']
    });

    this.rangoFechasFormGroup = this.fb.group({
      inicio: [null],
      fin: [null]
    });

    // filteredDocsSignal
    this.filteredDocsSignal = computed(() => {
      const docs = this.documentsSignal();
      const folioFilter = this.folioFilterSignal().toLowerCase();
      const statusFilter = this.statusFilterSignal();
      const startDate = this.startDateFilterSignal();
      const endDate = this.endDateFilterSignal();

      return docs.filter(doc => {
        const matchesFolio = doc.folio.toLowerCase().includes(folioFilter);
        const matchesStatus = statusFilter ? doc.status === statusFilter : true;
        let matchesDate = true;
        if (startDate || endDate) {
          const docDate = new Date(doc.createdAt);
          docDate.setHours(0, 0, 0, 0);
          if (startDate) {
            const start = new Date(startDate);
            start.setHours(0, 0, 0, 0);
            matchesDate = matchesDate && docDate >= start;
          }
          if (endDate) {
            const end = new Date(endDate);
            end.setHours(23, 59, 59, 999);
            matchesDate = matchesDate && docDate <= end;
          }
        }
        return matchesFolio && matchesStatus && matchesDate;
      }).sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime()); // Sort by Last Modified Descending
    });

    this.paginatedDocsSignal = computed(() => {
      const docs = this.filteredDocsSignal();
      const start = this.pageIndex() * this.pageSize();
      return docs.slice(start, start + this.pageSize());
    });

    // Effect de auto-refresh
    effect(() => {
      const currentState = this.analysisStatusSignal();
      if (this.previousIsProcessing && !currentState.is_processing) {
        this.loadData(true, true);
      }
      this.previousIsProcessing = currentState.is_processing;
    });

    // Effect para actualizar el contador de seleccionados
    effect(() => {
      // Dependencia implícita para refrescar si cambian los documentos
      this.documentsSignal();
      // Limpiar selección si los documentos ya no existen (opcional, por seguridad)
      const currentIds = new Set(this.documentsSignal().map(d => d.id));
      const newSelection = this.selection.selected.filter(id => currentIds.has(id));
      if (newSelection.length !== this.selection.selected.length) {
        this.selection.clear();
        this.selection.select(...newSelection);
        this.selectedCount.set(this.selection.selected.length);
      }
    });
  }

  private previousIsProcessing: boolean = false;

  ngOnInit(): void {
    this.loadData();
    this.analysisService.startPolling();
    this.setupFilterSubscriptions();
  }

  ngOnDestroy(): void {
    this.analysisService.stopPolling();
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadData(force: boolean = false, silent: boolean = false): void {
    this.externalDocumentService.loadDocuments(force, silent).subscribe({
      error: (err) => {
        if (!silent) this.snackBar.open('Error al cargar los documentos.', 'error');
      }
    });
  }

  handlePageEvent(e: PageEvent) {
    this.pageIndex.set(e.pageIndex);
    this.pageSize.set(e.pageSize);
  }

  onProcessorChange(event: any): void {
    this.selectedProcessor.set(event.value);
  }

  setupFilterSubscriptions() {
    this.folioFilterCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.folioFilterSignal.set(value || '');
      this.filtroForm.patchValue({ folio: value || '' }, { emitEvent: false });
      this.pageIndex.set(0);
      this.selection.clear(); this.selectedCount.set(0); // Limpiar selección al filtrar
    });

    this.statusFilterCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.statusFilterSignal.set(value || '');
      this.filtroForm.patchValue({ estatus: value || '' }, { emitEvent: false });
      this.pageIndex.set(0);
      this.selection.clear(); this.selectedCount.set(0);
    });

    this.startDateFilterCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.startDateFilterSignal.set(value);
      this.rangoFechasFormGroup.patchValue({ inicio: value }, { emitEvent: false });
      this.pageIndex.set(0);
      this.selection.clear(); this.selectedCount.set(0);
    });

    this.endDateFilterCtrl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
      this.endDateFilterSignal.set(value);
      this.rangoFechasFormGroup.patchValue({ fin: value }, { emitEvent: false });
      this.pageIndex.set(0);
      this.selection.clear(); this.selectedCount.set(0);
    });
  }

  // --- LÓGICA DE SELECCIÓN ---

  /** Si el número de elementos seleccionados coincide con el número total de filas visibles (filtradas) */
  /** Si el número de elementos seleccionados coincide con el número total de filas disponibles (no eliminando) */
  isAllSelected() {
    const deletingSet = this.deletingIds();
    const validRows = this.filteredDocsSignal().filter(row => !deletingSet.has(row.id));

    if (validRows.length === 0) return false;

    return validRows.every(row => this.selection.isSelected(row.id));
  }

  /** Selecciona todas las filas validas si no están todas seleccionadas; de lo contrario, limpia. */
  toggleAllRows() {
    if (this.isAllSelected()) {
      this.selection.clear();
    } else {
      const deletingSet = this.deletingIds();
      this.filteredDocsSignal().forEach(row => {
        if (!deletingSet.has(row.id)) {
          this.selection.select(row.id);
        }
      });
    }
    this.selectedCount.set(this.selection.selected.length);
  }

  toggleRow(rowId: string) {
    if (this.deletingIds().has(rowId)) return; // Prevención adicional
    this.selection.toggle(rowId);
    this.selectedCount.set(this.selection.selected.length);
  }


  // --- ACCIONES EN LOTE ---

  onAnalyzeSelected(): void {
    // Filtrar IDs que ya se están borrando
    const deletingSet = this.deletingIds();
    const selectedIds = this.selection.selected.filter(id => !deletingSet.has(id));

    if (selectedIds.length === 0) return;

    this.snackBar.open(`Encolando ${selectedIds.length} documentos para análisis...`, 'info');
    this.isProcessingBatch.set(true);

    this.externalDocumentService.analyzeDocuments(selectedIds, this.selectedProcessor())
      .subscribe({
        next: () => {
          this.snackBar.open('Documentos agregados a la cola de procesamiento.', 'success');
          this.selection.clear();
          this.selectedCount.set(0);
          this.isProcessingBatch.set(false);
        },
        error: (err) => {
          this.snackBar.open('Error al encolar documentos.', 'error');
          this.isProcessingBatch.set(false);
        }
      });
  }

  onResetSelected(): void {
    const selectedIds = this.selection.selected;
    if (selectedIds.length === 0) return;

    this.customMessage.open({
      title: 'Confirmar Reinicio Masivo',
      message: `¿Reiniciar ${selectedIds.length} documentos? Se borrarán los datos extraídos.`,
      type: 'warning',
      acceptBtn: true,
      dismissBtn: true
    }).then(confirmed => {
      if (confirmed) {
        this.isProcessingBatch.set(true);
        this.snackBar.open('Reiniciando documentos...', 'info');

        this.externalDocumentService.resetDocuments(selectedIds).subscribe({
          next: () => {
            this.snackBar.open('Documentos reiniciados correctamente.', 'success');
            this.selection.clear();
            this.selectedCount.set(0);
            this.isProcessingBatch.set(false);
          },
          error: () => {
            this.snackBar.open('Error al reiniciar algunos documentos.', 'error');
            this.isProcessingBatch.set(false);
          }
        });
      }
    });
  }

  onDeleteSelected(): void {
    const deletingSet = this.deletingIds();
    // Evitar re-enviar los que ya están en proceso
    const selectedIds = this.selection.selected.filter(id => !deletingSet.has(id));

    if (selectedIds.length === 0) return;

    this.customMessage.open({
      title: 'Confirmar Eliminación Masiva',
      message: `¿Eliminar ${selectedIds.length} documentos? Se borrarán los archivos y las facturas/remisiones generadas.`,
      type: 'error',
      acceptBtn: true,
      dismissBtn: true
    }).then(confirmed => {
      if (confirmed) {
        // 1. Limpiar selección inmediata (para que la UI se desbloquee)
        this.selection.clear();
        this.selectedCount.set(0);

        this.snackBar.open('Eliminación iniciada en segundo plano...', 'info');

        // 2. Delegar al servicio (No nos suscribimos aquí)
        this.externalDocumentService.initiateBackgroundDeletion(selectedIds);
      }
    });
  }

  startFullAnalysis(): void {
    this.snackBar.open(`Iniciando análisis completo (${this.selectedProcessor()})...`, 'info');
    this.analysisService.startFullAnalysis(this.selectedProcessor()).subscribe({
      next: () => this.snackBar.open('El proceso de análisis ha comenzado.', 'success'),
      error: (err) => this.snackBar.open('Error al iniciar el proceso.', 'error')
    });
  }


  // Helpers
  limpiarFiltros(): void {
    this.folioFilterCtrl.setValue('');
    this.statusFilterCtrl.setValue('');
    this.startDateFilterCtrl.setValue(null);
    this.endDateFilterCtrl.setValue(null);
    this.filtroForm.reset();
    this.rangoFechasFormGroup.reset();
    this.pageIndex.set(0);
    this.snackBar.open('Filtros limpiados', 'success');
  }

  goBack(): void {
    this.router.navigate(['/home']);
  }

  getAnalysisStatusColor(): string {
    const state = this.analysisStatusSignal();
    if (state.is_processing) {
      return 'accent';
    }
    if (state.history.length > 0 && state.history[0].status === 'ERROR') {
      return 'warn';
    }
    return 'primary';
  }

  onRemoveFile(doc: ExternalDocument, file: any): void {
    this.customMessage.open({
      title: 'Confirmar Eliminación de Archivo',
      message: `¿Eliminar archivo ${file.fileName}?`,
      type: 'warning',
      acceptBtn: true,
      dismissBtn: true
    }).then(confirmed => {
      if (confirmed) {
        this.snackBar.open(`Eliminando archivo...`, 'info');
        const identifier = file.url || file.path;
        if (!identifier) {
          this.snackBar.open('No se puede identificar el archivo para eliminar.', 'error');
          return;
        }

        this.externalDocumentService.removeFile(doc.id, identifier).subscribe({
          next: () => {
            this.snackBar.open('Archivo eliminado correctamente.', 'success');
          },
          error: (err) => this.snackBar.open('Error al eliminar el archivo.', 'error')
        });
      }
    });
  }

  onDownloadFile(doc: ExternalDocument, file: any): void {
    const identifier = file.url || file.path;

    if (!identifier) {
      this.snackBar.open('No se puede identificar el archivo para descargar.', 'error');
      return;
    }

    this.snackBar.open('Generando enlace seguro...', 'info');

    this.externalDocumentService.getDownloadUrl(doc.id, identifier).subscribe({
      next: (response) => {
        if (response && response.download_url) {
          window.open(response.download_url, '_blank');
          this.snackBar.open('Enlace generado correctamente', 'success');
        } else {
          this.snackBar.open('No se pudo generar el enlace de descarga.', 'error');
        }
      },
      error: (err) => {
        this.snackBar.open('Error al obtener el enlace de descarga.', 'error');
      }
    });
  }

  onViewLogs(doc: ExternalDocument): void {
    if (!doc.analysis_logs) {
      this.snackBar.open('No hay registros de análisis disponibles para este documento.', 'warning');
      return;
    }

    this.dialog.open(AnalysisDetailDialogComponent, {
      width: '800px',
      maxHeight: '90vh',
      data: doc.analysis_logs
    });
  }

}